import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import { Mail, Facebook, Linkedin, Twitter } from "lucide-react";
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton, WhatsappShareButton, EmailShareButton } from "react-share";
import { fetchSanity, sanityClient } from "../lib/sanity";
import { createPortableTextComponents } from "./portableTextSerializers";
import { blogPosts as mockBlogPosts } from "../mocks/mock";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";

const SHOULD_USE_MOCK_DATA = process.env.NODE_ENV !== "production";

const Whatsapp = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.5A11.8 11.8 0 0012 0 12 12 0 000 12a11.8 11.8 0 0017.37 10.38l2.63.83-.84-2.61A11.8 11.8 0 0024 12a11.8 11.8 0 00-3.48-8.5zM12 22a10 10 0 01-5-1.33L3 22l1.3-4.01A9.9 9.9 0 012 12a10 10 0 0110-10 9.8 9.8 0 017 3A10 10 0 0122 12a9.9 9.9 0 01-2.86 7.06 9.8 9.8 0 01-7.14 2.94z" />
    <path d="M17.57 14.1c-.3-.15-1.76-.86-2.03-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.48-1.76-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.03 1-1.03 2.45s1.05 2.84 1.2 3.03c.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.11.56-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

export default function BlogPostPage() {
  const { language, t, field } = useI18n();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const portableComponents = useMemo(
    () => (sanityClient ? createPortableTextComponents(sanityClient) : {}),
    [sanityClient]
  );

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      const data = await fetchSanity(
        '*[_type=="blogPost" && slug.current==$slug]{..., "previewImage": previewImage.asset->url, "images": images[].asset->url, "slug": slug.current}[0]',
        { slug }
      );
      if (!isMounted) return;

      if (data) {
        setPost(data);
      } else if (SHOULD_USE_MOCK_DATA) {
        const mock = mockBlogPosts.find((b) => b.slug === slug);
        setPost(mock || null);
      } else {
        setPost(null);
      }
      setLoading(false);
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) return <div className="p-6">{t("Loading...")}</div>;
  if (!post) return <div className="p-6">{t("Post not found.")}</div>;

  const getPreview = post.previewImage || (post.images && post.images[0]) || "";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post.title || "";

  return (
    <div className="bg-white text-zinc-900 min-h-screen">
      <main className="mx-auto max-w-3xl p-6">
        <div className="mb-6 flex justify-end"><LanguageSwitcher dark={false} /></div>
        <h1 className="text-3xl font-semibold mb-2">{field(post, "title")}</h1>
        <img src={getPreview} alt="cover" className="rounded-lg border border-zinc-200" />
        <p className="mt-2 text-sm text-zinc-500">
          {post.author} • {new Date(post.date).toLocaleDateString(language)} ({t("updated")}{" "}
          {new Date(post.lastUpdate).toLocaleDateString(language)})
        </p>
        <div className="mt-6 prose prose-zinc max-w-none">
          <PortableText value={field(post, "content")} components={portableComponents} />
        </div>
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {post.hashtags.map((tag) => (
              <span key={tag} className="text-sm text-zinc-600">#{tag}</span>
            ))}
          </div>
        )}
        {post.images && post.images.length > 1 && (
          <div className="mt-6 grid grid-cols-2 gap-2">
            {(post.previewImage ? post.images : post.images.slice(1)).map((img, idx) => (
              <img key={idx} src={img} alt={`img-${idx}`} className="rounded-lg border border-zinc-200" />
            ))}
          </div>
        )}
        <div className="mt-6 flex items-center gap-3 text-zinc-600">
          <span className="text-sm">{t("Share:")}</span>
          <WhatsappShareButton url={shareUrl} title={shareTitle}>
            <Whatsapp className="h-5 w-5" />
          </WhatsappShareButton>
          <EmailShareButton url={shareUrl} subject={shareTitle} body={shareUrl}>
            <Mail className="h-5 w-5" />
          </EmailShareButton>
          <FacebookShareButton url={shareUrl} quote={shareTitle}>
            <Facebook className="h-5 w-5" />
          </FacebookShareButton>
          <LinkedinShareButton url={shareUrl} title={shareTitle}>
            <Linkedin className="h-5 w-5" />
          </LinkedinShareButton>
          <TwitterShareButton url={shareUrl} title={shareTitle}>
            <Twitter className="h-5 w-5" />
          </TwitterShareButton>
        </div>
      </main>
    </div>
  );
}
